'use client'

import { Droplets, Pause, Play, Scissors, Sprout } from 'lucide-react'
import type { AtlasSpecies } from './atlas-data'
import {
  GRASS_SIMULATION_PRESETS,
  getGrassSimulationSamples,
  type GrassSimulationInput,
  type GrassSimulationPreset,
  type GrassSimulationVisualState,
  type SimulationMowing,
  type SimulationNutrients,
  type SimulationWater,
} from './grass-simulation'
import styles from './grass-atlas.module.css'

interface SimulationPanelProps {
  species: AtlasSpecies
  input: GrassSimulationInput
  state: GrassSimulationVisualState
  playing: boolean
  onPlayingChange: (playing: boolean) => void
  onDayChange: (day: number) => void
  onDayCommit: (day: number) => void
  onPresetSelect: (preset: GrassSimulationPreset) => void
  onConditionChange: (change: Partial<Omit<GrassSimulationInput, 'day'>>) => void
}

const WATER_OPTIONS: Array<{ value: SimulationWater; label: string }> = [
  { value: 'low', label: 'Too little' },
  { value: 'right', label: 'Enough' },
  { value: 'too-much', label: 'Too much' },
]
const MOWING_OPTIONS: Array<{ value: SimulationMowing; label: string }> = [
  { value: 'too-short', label: 'Too short' },
  { value: 'healthy', label: 'Healthy cut' },
  { value: 'let-grow', label: 'Let it grow' },
]
const NUTRIENT_OPTIONS: Array<{ value: SimulationNutrients; label: string }> = [
  { value: 'low', label: 'Too little' },
  { value: 'balanced', label: 'Enough' },
]

function TrendCard({
  title,
  value,
  suffix,
  values,
  day,
  current,
  max,
}: {
  title: string
  value: string
  suffix?: string
  values: Array<{ day: number; value: number }>
  day: number
  current: number
  max: number
}) {
  const width = 220
  const height = 68
  const inset = 7
  const x = (sampleDay: number) => inset + (sampleDay / 90) * (width - inset * 2)
  const y = (sampleValue: number) => height - inset - (Math.max(0, Math.min(max, sampleValue)) / max) * (height - inset * 2)
  const points = values.map((sample) => `${x(sample.day)},${y(sample.value)}`).join(' ')
  const markerX = x(day)
  const markerY = y(current)

  return (
    <article className={styles.simTrendCard}>
      <div>
        <span>{title}</span>
        <strong>{value}{suffix}</strong>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${title}: ${value}${suffix ?? ''} on day ${Math.round(day)}`}>
        <line x1={inset} y1={height - inset} x2={width - inset} y2={height - inset} />
        <polyline points={points} />
        <circle
          cx="0"
          cy="0"
          r="4.5"
          style={{ transform: `translate(${markerX}px, ${markerY}px)` }}
        />
      </svg>
    </article>
  )
}

function ConditionGroup<T extends string>({
  label,
  icon,
  value,
  options,
  onChange,
}: {
  label: string
  icon: React.ReactNode
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <fieldset className={styles.simCondition}>
      <legend>{icon}{label}</legend>
      <div>
        {options.map((option) => (
          <button
            type="button"
            key={option.value}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

export function GrassSimulationPanel({
  species,
  input,
  state,
  playing,
  onPlayingChange,
  onDayChange,
  onDayCommit,
  onPresetSelect,
  onConditionChange,
}: SimulationPanelProps) {
  const samples = getGrassSimulationSamples(input, species.simulationBaseline)
  const selectedPreset = GRASS_SIMULATION_PRESETS.find((preset) =>
    preset.water === input.water && preset.mowing === input.mowing && preset.nutrients === input.nutrients,
  )?.id

  return (
    <aside className={`${styles.detailPanel} ${styles.simulationPanel}`} aria-label={`${species.name} living lawn lab`}>
      <span className={styles.eyebrow}>Grass growth lab · {species.name}</span>
      <h2 className={styles.panelTitle}>See how care changes the lawn</h2>
      <p className={styles.panelIntro}>Press play to watch 90 days. Try water, mowing, and plant food to see what changes.</p>

      <div className={styles.simPlayer}>
        <button
          type="button"
          className={styles.simPlayButton}
          aria-label={playing ? 'Pause simulation' : input.day >= 90 ? 'Play simulation from the beginning' : 'Play simulation'}
          onClick={() => onPlayingChange(!playing)}
        >
          {playing ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" />}
        </button>
        <label>
          <span><strong>Day {Math.round(input.day)}</strong><small>of 90</small></span>
          <input
            type="range"
            min="0"
            max="90"
            step="1"
            value={input.day}
            onChange={(event) => onDayChange(Number(event.target.value))}
            onPointerUp={(event) => onDayCommit(Number(event.currentTarget.value))}
            onKeyUp={(event) => onDayCommit(Number(event.currentTarget.value))}
            aria-label="Simulation day"
          />
        </label>
      </div>

      <div className={styles.simPresets} aria-label="Simulation examples">
        {GRASS_SIMULATION_PRESETS.map((preset) => (
          <button
            type="button"
            key={preset.id}
            aria-pressed={selectedPreset === preset.id}
            onClick={() => onPresetSelect(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className={styles.simConditions}>
        <ConditionGroup label="Water" icon={<Droplets size={14} />} value={input.water} options={WATER_OPTIONS} onChange={(water) => onConditionChange({ water })} />
        <ConditionGroup label="Mowing" icon={<Scissors size={14} />} value={input.mowing} options={MOWING_OPTIONS} onChange={(mowing) => onConditionChange({ mowing })} />
        <ConditionGroup label="Plant food" icon={<Sprout size={14} />} value={input.nutrients} options={NUTRIENT_OPTIONS} onChange={(nutrients) => onConditionChange({ nutrients })} />
      </div>

      <div className={styles.simResponse} aria-live="polite">
        <span>{state.stage} · {state.visualCue}</span>
        <p>{state.message}</p>
      </div>

      <div className={styles.simTrends}>
        <TrendCard
          title="Blade height"
          value={state.heightInches.toFixed(1)}
          suffix=" in"
          day={input.day}
          current={state.heightInches}
          max={Math.max(6, species.simulationBaseline.mowingInches.optimumMax * 1.7)}
          values={samples.map((sample) => ({ day: sample.day, value: sample.state.heightInches }))}
        />
        <TrendCard
          title="Plant color"
          value={String(state.plantColorScore)}
          suffix=" / 100"
          day={input.day}
          current={state.plantColorScore}
          max={100}
          values={samples.map((sample) => ({ day: sample.day, value: sample.state.plantColorScore }))}
        />
        <TrendCard
          title="Root energy"
          value={String(state.rootEnergyScore)}
          suffix=" / 100"
          day={input.day}
          current={state.rootEnergyScore}
          max={100}
          values={samples.map((sample) => ({ day: sample.day, value: sample.state.rootEnergyScore }))}
        />
      </div>

      <p className={styles.simBoundary}>Teaching simulation, not a forecast. Weather, soil, cultivar, and timing change real lawns.</p>
    </aside>
  )
}
